import React, { useCallback, useRef } from 'react';
import cx from 'classnames';
import PropTypes from 'prop-types';

import { SiteConfigManager, useSettings } from '@atlas/core';
import { Flag, Icon } from '@atlas/ui';

import { CDN_TYPE_BASE, getCDNUrl } from 'common/utils';
import { languageTypeFlag, languageTypeISO } from 'src/common/constants';

/**
 * Hook that alerts clicks outside of the passed ref
 */
export const useDropdown = (ref, onToggle) => {
  React.useEffect(() => {
    /**
     * Alert if clicked on outside of element
     */
    const handleClickOutside = e => {
      if (ref.current && !ref.current.contains(e.target)) {
        onToggle(e);
      }
    };

    // Bind the event listener
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [ref]);
};

export function DropdownLanguageWrapper({
  buttonText,
  children,
  className,
  disabled = false,
  isActive,
  isBg = false,
  isCloseOnClickInside,
  setExpanded,
  toggleRef,
}) {
  const wrapperRef = useRef(null);
  const buttonRef = useRef(null);

  const onClickDropdownSwitcher = useCallback(
    e => {
      if (disabled) return;

      if (buttonRef && buttonRef.current.contains(e.target)) {
        setExpanded(isExpanded => !isExpanded);
        return;
      }

      if (!isCloseOnClickInside) {
        setExpanded(true);
        return;
      }

      if (toggleRef && !toggleRef.current.contains(e.target)) {
        return;
      }
      setExpanded(isExpanded => !isExpanded);
    },
    [disabled, setExpanded, toggleRef, isCloseOnClickInside, buttonRef],
  );

  const closeOnClickOut = useCallback(() => {
    setExpanded(isExpanded => {
      if (isExpanded) {
        return !isExpanded;
      }
      return isExpanded;
    });
  }, [setExpanded]);

  useDropdown(wrapperRef, closeOnClickOut);

  const dropdownClassName = cx('dropdown', isBg && 'bg', className);

  const dropdownButtonClassName = cx(
    'dropdown-button',
    isActive && 'active',
    disabled && 'disabled',
  );
  const { lang } = useSettings();
  const languages = React.useMemo(() => SiteConfigManager.getConfig('languages', []), []);
  const currentRegion = languages.find(({ code }) => code === lang);
  const cdnHostname = getCDNUrl(CDN_TYPE_BASE);

  const desktopLanguageType = SiteConfigManager.getConfig(
    'config.config.language.desktopLanguageType',
  );

  const isLanguageTypeFlag = desktopLanguageType === languageTypeFlag;
  const isLanguageTypeISO = desktopLanguageType === languageTypeISO;

  const singleLangClassName = cx(
    'single-language-block',
    { 'only-flag': isLanguageTypeFlag && languages.length === 1 },
    { 'only-code': isLanguageTypeISO && languages.length === 1 },
  );

  if (languages.length === 1) {
    return (
      <div className={singleLangClassName}>
        <div className="dropdown-button__flag">
          {currentRegion && (
            <Flag
              alt={currentRegion.name}
              cdnHostname={cdnHostname}
              height={15}
              name={currentRegion.regionId}
              width={22}
            />
          )}
        </div>
        <div className="dropdown-button__text">{buttonText}</div>
      </div>
    );
  }

  return (
    <div
      className={dropdownClassName}
      onClick={onClickDropdownSwitcher}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClickDropdownSwitcher(e);
        }
      }}
      ref={wrapperRef}
      role="button"
      tabIndex={0}
    >
      <button className={dropdownButtonClassName} ref={buttonRef} type="button">
        <div className="dropdown-button__flag">
          {currentRegion && (
            <Flag
              alt={currentRegion.name}
              cdnHostname={cdnHostname}
              height={15}
              name={currentRegion.regionId}
              width={22}
            />
          )}
        </div>

        <div className="dropdown-button__text">{buttonText}</div>

        <Icon
          className="dropdown-button__icon-right"
          name={disabled ? 'lock' : isActive ? 'arrow-up' : 'arrow-down'}
        />
      </button>

      {isActive && (
        <div className="dropdown-list">
          <div className="dropdown-list__wrapper">{children}</div>
        </div>
      )}
    </div>
  );
}

DropdownLanguageWrapper.propTypes = {
  buttonText: PropTypes.string,
  children: PropTypes.node,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  isActive: PropTypes.bool,
  isBg: PropTypes.bool,
  isCloseOnClickInside: PropTypes.bool,
  setExpanded: PropTypes.func,
  toggleRef: PropTypes.object,
};
